import { useState, useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import { apiFetch } from '../utils/api';
import { teamAcronym } from '../utils/teamAcronyms';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const NEIGHBOR_COLORS = [
  'rgba(220, 53, 69, 0.8)',
  'rgba(25, 135, 84, 0.8)',
  'rgba(255, 153, 0, 0.85)',
  'rgba(111, 66, 193, 0.8)',
  'rgba(0, 188, 212, 0.8)',
  'rgba(214, 51, 132, 0.8)',
  'rgba(108, 117, 125, 0.8)',
  'rgba(253, 126, 20, 0.8)',
  'rgba(32, 201, 151, 0.8)',
  'rgba(13, 202, 240, 0.8)',
];

function formatDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

export default function PriceHistoryCard() {
  const { token } = useAuth();
  const { selectedTeam, selectedDate } = useDashboard();
  const [market, setMarket] = useState(null);
  const [markets, setMarkets] = useState([]);
  const [traces, setTraces] = useState([]);
  const [traceType, setTraceType] = useState('market');
  const [noData, setNoData] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setMarket(null);
  }, [selectedTeam]);

  useEffect(() => {
    if (!token || !selectedTeam || !selectedDate) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ team: selectedTeam, date: selectedDate });
    if (market) params.set('market', market);

    apiFetch(`/price-history?${params}`, token)
      .then((data) => {
        if (cancelled) return;
        setMarkets(data.markets);
        setTraces(data.traces);
        setTraceType(data.type ?? 'market');
        setNoData(data.noData);
        if (!market && data.market) setMarket(data.market);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [token, selectedTeam, selectedDate, market]);

  const chartData = useMemo(() => {
    if (!traces.length) return null;

    const allDates = [...new Set(traces.flatMap((t) => t.points.map((p) => p.date)))].sort();

    return {
      labels: allDates.map(formatDate),
      datasets: (() => {
        let colorIdx = 0;
        return traces.map((trace) => {
          const dateMap = Object.fromEntries(trace.points.map((p) => [p.date, p.value]));
          const dataPoints = allDates.map((d) => dateMap[d] ?? null);
          if (trace.selected) {
            return {
              label: teamAcronym(trace.team),
              data: dataPoints,
              borderColor: 'rgba(13, 110, 253, 1)',
              borderWidth: 3.5,
              pointRadius: 0,
              pointBackgroundColor: 'rgba(13, 110, 253, 1)',
              tension: 0.3,
              spanGaps: true,
              order: 0,
            };
          }
          const color = NEIGHBOR_COLORS[colorIdx++ % NEIGHBOR_COLORS.length];
          return {
            label: teamAcronym(trace.team),
            data: dataPoints,
            borderColor: color,
            borderWidth: 2,
            pointRadius: 0,
            pointBackgroundColor: color,
            tension: 0.3,
            spanGaps: true,
            order: 1,
          };
        });
      })(),
    };
  }, [traces]);

  const chartOptions = useMemo(() => {
    const isMarket = traceType === 'market';

    let yMin, yMax;
    if (traces.length) {
      const allValues = traces.flatMap((t) => t.points.map((p) => p.value)).filter((v) => v != null);
      if (allValues.length) {
        const dataMin = Math.min(...allValues);
        const dataMax = Math.max(...allValues);
        const range = dataMax - dataMin;
        const padding = Math.max(range * 0.15, 0.01);
        yMin = isMarket ? Math.max(0, dataMin - padding) : dataMin - padding;
        yMax = isMarket ? Math.min(1, dataMax + padding) : dataMax + padding;
      }
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true, position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
        tooltip: {
          callbacks: {
            label: (ctx) => isMarket
              ? `${ctx.dataset.label}: ${(ctx.parsed.y * 100).toFixed(1)}%`
              : `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(2)}`,
          },
        },
      },
      scales: {
        x: { ticks: { font: { size: 10 }, maxTicksLimit: 10 } },
        y: {
          ticks: {
            font: { size: 10 },
            ...(isMarket && { callback: (v) => `${(v * 100).toFixed(0)}%` }),
          },
          ...(yMin !== undefined && { min: yMin }),
          ...(yMax !== undefined && { max: yMax }),
        },
      },
    };
  }, [traceType, traces]);

  const marketLabel = markets.find((m) => m.name === market)?.label ?? market;

  return (
    <div className="card h-100">
      <div className="card-header d-flex align-items-center gap-2">
        <strong className="me-auto">Price History</strong>
        {markets.length > 0 && (
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={market ?? ''}
            onChange={(e) => setMarket(e.target.value)}
          >
            {markets.map((m) => (
              <option key={m.name} value={m.name}>{m.label}</option>
            ))}
          </select>
        )}
      </div>
      <div className="card-body">
        {loading && (
          <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>
        )}
        {error && (
          <div className="text-center py-4 text-danger small">{error}</div>
        )}
        {!loading && !error && noData && (
          <div className="text-center py-4 text-muted small">No data available</div>
        )}
        {!loading && !error && !noData && chartData && (
          <div style={{ height: '220px' }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        )}
      </div>
    </div>
  );
}
